import type { FormEvent } from "react";

import { Button, ButtonTypes } from "../basic/button";
import { Loading } from "../basic/loading";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../basic/otp";
import { ToggleSlider } from "../basic/toggleSlider";
import { LoginBackground } from "./background";
import { MfaOption } from "./mfaType";

interface Props {
	password: string;
	setPassword: (value: string) => void;
	onPaste?: () => void;
	submitFunction: (event?: FormEvent<HTMLFormElement>) => void;

	rememberMe?: boolean;
	setRememberMe?: (value: boolean) => void;

	resendCode?: () => void;
	resendLoading?: boolean;
	type?: MfaOption;
	obfuscation?: string;

	goBack?: () => void;
	loading: boolean;
	disabled: boolean;
	version?: string;
	error?: string;
	success?: string;

	appLogo?: string;
	backgroundImage?: string;
}

export function OneTimePasswordPage(props: Props) {
	const {
		password,
		setPassword,
		onPaste,
		submitFunction,
		rememberMe,
		setRememberMe,
		resendCode,
		resendLoading,
		type,
		obfuscation,
		goBack,
		loading,
		disabled,
		version,
		error,
		success,
		appLogo,
		backgroundImage,
	} = props;

	const typeString = type === MfaOption.Sms ? "your phone" : "your email";
	const obfuscationString = obfuscation ? ` (${obfuscation})` : "";

	return (
		<LoginBackground
			title="Multi Factor Code"
			description={`Your code was sent to ${typeString}${obfuscationString}. Enter it below to login`}
			version={version}
			appLogo={appLogo}
			backgroundImage={backgroundImage}
		>
			<form className="grid gap-4" onSubmit={submitFunction}>
				<div className="flex items-center justify-center gap-2">
					<InputOTP maxLength={6} value={password} onChange={setPassword} autoFocus onPaste={onPaste}>
						<InputOTPGroup>
							<InputOTPSlot index={0} />
							<InputOTPSlot index={1} />
							<InputOTPSlot index={2} />
							<InputOTPSlot index={3} />
							<InputOTPSlot index={4} />
							<InputOTPSlot index={5} />
						</InputOTPGroup>
					</InputOTP>
				</div>
				{error !== undefined && <span className="text-sm text-red-400">{error}</span>}
				{success !== undefined && <span className="text-sm text-green-400">{success}</span>}
				{rememberMe !== undefined && (
					<ToggleSlider
						id={"rememberMeCheckbox"}
						label={"Remember Machine"}
						value={rememberMe}
						changeHandler={setRememberMe}
						hideLabel
						inline
						rounded
						className="!m-0"
					/>
				)}
				<Loading loading={resendLoading} message="Sending code..." tag="h5">
					{resendCode && (
						<Button className="w-full" types={[ButtonTypes.Text]} onClick={resendCode} disabled={loading}>
							Resend Code
						</Button>
					)}
				</Loading>
				<Loading loading={loading} message="Verifying code..." tag="h5">
					<Button
						type="submit"
						className="w-full"
						types={[ButtonTypes.Primary]}
						disabled={disabled || loading || resendLoading}
					>
						Submit
					</Button>
				</Loading>

				{goBack && (
					<Button className="w-full" types={[ButtonTypes.Outline]} onClick={goBack}>
						Go Back
					</Button>
				)}
			</form>
		</LoginBackground>
	);
}
